import { useEffect, type FormEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, useWatch, type Control } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
	Button,
	CheckboxField,
	Dialog, DialogContent, DialogHeader,
	Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
	RadioGroup, RadioGroupItem,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import {
	automationUpsertSchema,
	useGetPropertyQuery,
	useUpdateRentalAutomationMutation,
} from '@/api'
import { RentalSummaryBar } from '@/components/rental-summary-bar'
import {
	UTILITY_CALCULATION_KEYS,
	UTILITY_PAID_BY_KEYS,
	UTILITY_TYPE_KEYS,
} from '@/types'
import type {
	AutomationAccrualDistribution,
	AutomationActFormation,
	AutomationInvoiceFormation,
	AutomationPaymentDistribution,
	AutomationSendMode,
	AutomationUpsertInput,
	Rental,
	RentalAutomation,
	UtilityType,
} from '@/types'

interface AutomationSetupDialogProps {
	open:           boolean
	onOpenChange:   (open: boolean) => void
	rentalId:       string
	rental?:        Rental
	initial?:       RentalAutomation
}

const DAYS_BEFORE_OPTIONS    = [1, 2, 3, 5, 7, 10, 14] as const
const REPEAT_EVERY_OPTIONS   = [1, 2, 3, 5, 7]         as const
const REPEAT_MAX_OPTIONS     = [1, 2, 3, 5, 10]        as const
const COMPLETION_OPTIONS     = [7, 14, 30, 60, 90]     as const
const PAYMENT_DAY_OPTIONS    = [1, 5, 10, 15, 20, 25, 28] as const

function buildDefaults(initial?: RentalAutomation): AutomationUpsertInput {
	return {
		paymentDay:                 initial?.paymentDay                 ?? 10,
		invoiceFormation:           (initial?.invoiceFormation           ?? 'DAYS_BEFORE_PAYMENT') as AutomationInvoiceFormation,
		invoiceFormationDaysBefore: initial?.invoiceFormationDaysBefore  ?? 3,
		sendToTenantMode:           (initial?.sendToTenantMode           ?? 'AUTOMATIC') as AutomationSendMode,
		sendToTenantAt:             initial?.sendToTenantAt              ?? undefined,
		actFormation:               (initial?.actFormation               ?? 'ON_INVOICE_ISSUE') as AutomationActFormation,
		accrualDistribution:        (initial?.accrualDistribution        ?? 'ALL_IN_ONE') as AutomationAccrualDistribution,
		reminderBeforeDaysBefore:   initial?.reminderBeforeDaysBefore    ?? 3,
		remindOnDueDate:            initial?.remindOnDueDate             ?? true,
		repeatReminderEveryDays:    initial?.repeatReminderEveryDays     ?? 3,
		repeatReminderMaxTimes:     initial?.repeatReminderMaxTimes      ?? 2,
		paymentDistribution:        (initial?.paymentDistribution        ?? 'RENT_FIRST') as AutomationPaymentDistribution,
		utilityReadingsRequestDaysBefore: initial?.utilityReadingsRequestDaysBefore ?? 3,
		utilityRemindOnCalculationDay:    initial?.utilityRemindOnCalculationDay    ?? true,
		completionReminderDaysBefore:     initial?.completionReminderDaysBefore     ?? 30,
	}
}

function formatMoney(amount?: string, currency?: string): string {
	if (!amount) return '—'
	const num = Number(amount)
	if (!Number.isFinite(num)) return amount
	return `${num.toLocaleString('ru-RU')} ${currency === 'RUB' ? '₽' : currency ?? ''}`.trim()
}

function formatDateShort(iso?: string): string {
	if (!iso) return '—'
	return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatRuLong(d: Date): string {
	return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
}

/** Next occurrence of `day` (1–31) in calendar — current month if not yet passed, else next month. */
function nextOccurrence(day: number): Date {
	const now = new Date()
	const target = new Date(now.getFullYear(), now.getMonth(), Math.max(1, Math.min(31, day || 1)))
	if (target < now) target.setMonth(target.getMonth() + 1)
	return target
}

function addDays(d: Date, days: number): Date {
	const r = new Date(d)
	r.setDate(r.getDate() + days)
	return r
}

// UtilityType → icon URL. Icons live in `/public/logos/Icon + Bg{7,8,9,10png}.png`.
// GAS / INTERNET / OTHER use a generic glyph (Bg4 = wallet) until dedicated assets ship.
const UTILITY_ICONS: Record<UtilityType, string> = {
	ELECTRICITY: '/logos/Icon + Bg7.png',
	WATER:       '/logos/Icon + Bg8.png',
	HEATING:     '/logos/Icon + Bg9.png',
	WASTE:       '/logos/Icon + Bg10png.png',
	GAS:         '/logos/Icon + Bg4.png',
	INTERNET:    '/logos/Icon + Bg4.png',
	OTHER:       '/logos/Icon + Bg4.png',
}

export function AutomationSetupDialog({ open, onOpenChange, rentalId, rental, initial }: AutomationSetupDialogProps) {
	const { t } = useTranslation()
	const isEdit = !!initial

	const form = useForm<AutomationUpsertInput>({
		resolver:      zodResolver(automationUpsertSchema),
		defaultValues: buildDefaults(initial),
		mode:          'onBlur',
	})

	// Re-seed form when the dialog re-opens or the underlying record changes.
	// `defaultValues` only run once on mount; `reset()` is the proper way to swap.
	// Keyed on `initial?.id` so editing existing automation reflects fresh server data
	// after RTK refetch, and switching to a different rental clears the previous draft.
	useEffect(() => {
		if (open) form.reset(buildDefaults(initial))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, initial?.id])

	const [upsert, { isLoading, isError }] = useUpdateRentalAutomationMutation()

	// Pull live property — utilities/services live on Property, not Rental.
	// Skip the request when there's no propertyId yet (early-stage rental).
	const { data: property } = useGetPropertyQuery(rental?.propertyId ?? '', {
		skip: !rental?.propertyId,
	})
	const utilities          = property?.utilities          ?? []
	const additionalServices = property?.additionalServices ?? []

	async function onSubmit(values: AutomationUpsertInput) {
		try {
			await upsert({ rentalId, body: values }).unwrap()
			onOpenChange(false)
		} catch (err) {
			console.error('upsertAutomation failed:', err)
		}
	}

	function onFormSubmit(e: FormEvent<HTMLFormElement>) {
		void form.handleSubmit(onSubmit)(e)
	}

	const penaltyLabel = rental?.penaltyRate ? `${rental.penaltyRate}% ${t('rent.terms.basic.perDay').toLowerCase()}` : '—'

	return (
		<Dialog open={open} onOpenChange={onOpenChange} disablePointerDismissal>
			<DialogContent
				showCloseButton={false}
				className="flex flex-col p-0 gap-0 overflow-hidden rounded-(--radius-lg)"
				style={{ width: 1218, maxWidth: '95vw', maxHeight: '90vh' }}
			>
				<DialogHeader
					border
					title={isEdit ? t('rent.automation.setupDialog.editTitle') : t('rent.automation.setupDialog.title')}
					subtitle={t('rent.automation.setupDialog.subtitle')}
				/>

				<Form {...form}>
					<form onSubmit={onFormSubmit} className="flex flex-1 flex-col overflow-hidden">
						<div className="flex flex-col gap-6 overflow-y-auto px-6 py-6">
							{/* Contract context bar — shared accent-tile header (see RentalSummaryBar) */}
							<div className="flex flex-col gap-3">
								<div className="flex items-baseline gap-2">
									<span className="text-sm font-[500] text-(--color-foreground)">
										{t('rent.automation.setupDialog.contract', { number: rental?.id.slice(-4) ?? '—' })}
									</span>
									<span className="text-xs text-(--color-muted-foreground)">
										{t('rent.automation.setupDialog.contractFrom', { date: formatDateShort(rental?.createdAt) })}
									</span>
								</div>
								{rental && <RentalSummaryBar rental={rental} />}
							</div>

							<div className="flex flex-1 gap-8">
								{/* Left context column — read-only rental snapshot */}
								<aside className="flex w-[300px] shrink-0 flex-col gap-6 rounded-(--radius-md) border border-(--color-border) p-4">
									<section className="flex flex-col gap-3">
										<h3 className="text-sm font-[500] text-(--color-foreground)">
											{t('rent.terms.basic.title')}
										</h3>
										<div className="grid grid-cols-[1fr_1fr] gap-x-3 gap-y-1">
											<div className="flex flex-col gap-0.5">
												<span className="text-xs text-(--color-muted-foreground)">
													{t('rent.automation.setupDialog.monthly')}
												</span>
												<span className="text-sm font-[500] text-(--color-foreground)">
													{formatMoney(rental?.rentAmount, rental?.currency)}
												</span>
											</div>
											<div className="flex flex-col gap-0.5">
												<span className="text-xs text-(--color-muted-foreground)">
													{t('rent.terms.basic.penalty')}
												</span>
												<span className="text-sm font-[500] text-(--color-foreground)">
													{penaltyLabel}
												</span>
											</div>
										</div>
									</section>

									<section className="flex flex-col gap-3">
										<h3 className="text-sm font-[500] text-(--color-foreground)">
											{t('rent.terms.utilities.title')}
										</h3>
										{utilities.length === 0 ? (
											<p className="text-xs text-(--color-muted-foreground)">
												{t('rent.terms.utilities.empty')}
											</p>
										) : (
											<ul className="flex flex-col gap-3">
												{utilities.map((u) => {
													const isIncluded = u.calculation === 'INCLUDED'
													return (
														<li key={u.id} className="flex items-start gap-3">
															<img src={UTILITY_ICONS[u.type]} alt="" className="size-9 shrink-0" />
															<div className="flex min-w-0 flex-1 flex-col gap-0.5">
																<span className="text-sm font-[500] text-(--color-foreground)">
																	{t(UTILITY_TYPE_KEYS[u.type])}
																</span>
																<div className="grid grid-cols-2 gap-x-2">
																	<span className="text-xs text-(--color-muted-foreground)">Расчет</span>
																	{!isIncluded && (
																		<span className="text-xs text-(--color-muted-foreground)">Оплачивает</span>
																	)}
																	<span className="text-xs text-(--color-foreground) truncate">
																		{t(UTILITY_CALCULATION_KEYS[u.calculation])}
																	</span>
																	{!isIncluded && (
																		<span className="text-xs text-(--color-foreground) truncate">
																			{t(UTILITY_PAID_BY_KEYS[u.paidBy])}
																		</span>
																	)}
																</div>
															</div>
														</li>
													)
												})}
											</ul>
										)}
									</section>

									<section className="flex flex-col gap-3">
										<h3 className="text-sm font-[500] text-(--color-foreground)">
											{t('rent.terms.additional.title')}
										</h3>
										{additionalServices.length === 0 ? (
											<p className="text-xs text-(--color-muted-foreground)">
												{t('rent.terms.additional.empty')}
											</p>
										) : (
											<ul className="flex flex-col gap-3">
												{additionalServices.map((s) => (
													<li key={s.id} className="flex items-start gap-3">
														<img src="/logos/Icon + Bg4.png" alt="" className="size-9 shrink-0" />
														<div className="flex min-w-0 flex-1 flex-col gap-0.5">
															<span className="text-sm font-[500] text-(--color-foreground)">{s.name}</span>
															<div className="grid grid-cols-2 gap-x-2">
																<span className="text-xs text-(--color-muted-foreground)">Цена за ед.</span>
																<span className="text-xs text-(--color-muted-foreground)">Кол-во</span>
																<span className="text-xs text-(--color-foreground)">
																	{formatMoney(s.pricePerUnit, s.currency)}
																</span>
																<span className="text-xs text-(--color-foreground)">
																	{`${s.quantity} ${s.unit}`}
																</span>
															</div>
														</div>
													</li>
												))}
											</ul>
										)}
									</section>
								</aside>

								{/* Right form column — 5 sections in one bordered card */}
								<div className="flex flex-1 flex-col rounded-(--radius-md) border border-(--color-border) p-6">
									<AccrualsBlock control={form.control} />
									<SectionDivider />
									<RemindersBlock control={form.control} />
									<SectionDivider />
									<PaymentsBlock rental={rental} />
									<SectionDivider />
									<UtilitiesBlock />
									<SectionDivider />
									<CompletionBlock rental={rental} />
								</div>
							</div>
						</div>

						<div className="flex items-center justify-end gap-2 border-t border-(--color-border) px-6 py-4">
							{isError && (
								<span className="mr-auto text-sm text-(--color-destructive)">
									{t('rent.automation.setupDialog.errors.save')}
								</span>
							)}
							<Button type="button" variant="secondary" onClick={() => onOpenChange(false)} disabled={isLoading}>
								{t('rent.automation.setupDialog.cancel')}
							</Button>
							<Button type="submit" disabled={isLoading}>
								{isLoading
									? t('common.saving', { defaultValue: 'Сохраняем…' })
									: isEdit
										? t('rent.automation.setupDialog.submitEdit')
										: t('rent.automation.setupDialog.submit')}
							</Button>
						</div>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}

// ── Sections ───────────────────────────────────────────────────────────────

function SectionDivider() {
	return <div className="my-6 h-px w-full bg-(--color-border)" />
}

function DialogSection({
	title, chips, children,
}: { title: string; chips?: ReactNode; children: ReactNode }) {
	return (
		<section className="grid grid-cols-[200px_1fr] gap-6">
			<h3 className="pt-1 text-base font-[500] text-(--color-foreground)">{title}</h3>
			<div className="flex flex-col gap-4">
				{chips}
				{children}
			</div>
		</section>
	)
}

function SummaryChip({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-sm text-(--color-muted-foreground)">{label}</span>
			<span className="text-sm font-[500] text-(--color-foreground)">{value}</span>
		</div>
	)
}

function SummaryChips({ children }: { children: ReactNode }) {
	return (
		<div className="grid grid-cols-2 gap-6 rounded-(--radius-md) bg-(--color-info)/10 px-5 py-4">
			{children}
		</div>
	)
}

/**
 * Выбор из всех значений перечисления.
 *
 * Раньше здесь стояла галочка, а перечисления трёхзначные: `ON_CONFIRMATION`
 * у отправки и `MANUAL` у акта выразить было нечем. Хранимую настройку с таким
 * значением галочка показывала снятой, и первое же переключение переписывало
 * её на одно из двух, между которыми она умела ходить.
 */
/** Значения из Prisma-перечислений: галочка выражала два из трёх. */
const SEND_TO_TENANT_MODES = ['AUTOMATIC', 'ON_CONFIRMATION', 'MANUAL'] as const
const ACT_FORMATIONS       = ['ON_INVOICE_ISSUE', 'ON_PAYMENT', 'MANUAL'] as const

/**
 * Выбор из всех значений перечисления.
 *
 * Раньше здесь стояла галочка, а перечисления трёхзначные: `ON_CONFIRMATION`
 * у отправки и `MANUAL` у акта выразить было нечем. Хранимую настройку с таким
 * значением галочка показывала снятой, и первое же переключение переписывало
 * её на одно из двух, между которыми она умела ходить.
 *
 * Разметка — как у «Распределения начислений» ниже: тот же ряд обрамлённых
 * строк, иначе один диалог показывал бы два разных вида выбора.
 */
function EnumRadioField({
	name, label, values, optionLabel,
}: {
	name:        keyof AutomationUpsertInput
	label:       string
	values:      readonly string[]
	optionLabel: (value: string) => string
}) {
	return (
		<FormField
			name={name}
			render={({ field }) => (
				<FormItem className="flex flex-col gap-2">
					<FormLabel className="text-sm text-(--color-muted-foreground)">{label}</FormLabel>
					<FormControl>
						<RadioGroup
							value={typeof field.value === 'string' ? field.value : undefined}
							onValueChange={field.onChange}
							className="flex flex-col gap-2"
						>
							{values.map((value) => (
								<label
									key={value}
									spellCheck={false}
									className="flex items-center gap-2 rounded-(--radius-md) border border-(--color-border) px-4 py-3 cursor-pointer select-none"
								>
									<RadioGroupItem value={value} />
									<span className="text-sm">{optionLabel(value)}</span>
								</label>
							))}
						</RadioGroup>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}

function DaysBeforeSelect({ name, label }: { name: keyof AutomationUpsertInput; label: string }) {
	const { t } = useTranslation()
	return (
		<FormField
			name={name}
			render={({ field }) => (
				<FormItem className="flex flex-col gap-1.5">
					<FormLabel className="text-sm text-(--color-muted-foreground)">{label}</FormLabel>
					<Select
						value={field.value != null ? String(field.value) : ''}
						onValueChange={(v) => field.onChange(Number(v))}
					>
						<FormControl>
							<SelectTrigger size="md">
								<SelectValue />
							</SelectTrigger>
						</FormControl>
						<SelectContent>
							{DAYS_BEFORE_OPTIONS.map((d) => (
								<SelectItem key={d} value={String(d)}>
									{t('rent.automation.setupDialog.daysBeforeOption', { count: d })}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</FormItem>
			)}
		/>
	)
}

function AccrualsBlock({ control }: { control: Control<AutomationUpsertInput> }) {
	const { t } = useTranslation()
	const paymentDay = useWatch({ control, name: 'paymentDay' }) ?? 10
	const invoiceFormationDaysBefore = useWatch({ control, name: 'invoiceFormationDaysBefore' }) ?? 0
	const dueDate = nextOccurrence(paymentDay)
	const formationDate = addDays(dueDate, -invoiceFormationDaysBefore)

	return (
		<DialogSection
			title={t('rent.automation.sections.accruals')}
			chips={(
				<SummaryChips>
					<SummaryChip label="Формирование счета" value={formatRuLong(formationDate)} />
					<SummaryChip label="День оплаты" value={formatRuLong(dueDate)} />
				</SummaryChips>
			)}
		>
			<FormField
				name="paymentDay"
				render={({ field }) => (
					<FormItem className="flex flex-col gap-1.5">
						<FormLabel className="text-sm text-(--color-muted-foreground)">
							{t('rent.automation.fields.paymentDay')}
						</FormLabel>
						<Select
							value={field.value != null ? String(field.value) : ''}
							onValueChange={(v) => field.onChange(Number(v))}
						>
							<FormControl>
								<SelectTrigger size="md">
									<SelectValue />
								</SelectTrigger>
							</FormControl>
							<SelectContent>
								{PAYMENT_DAY_OPTIONS.map((d) => (
									<SelectItem key={d} value={String(d)}>{`${d}-е число месяца`}</SelectItem>
								))}
							</SelectContent>
						</Select>
					</FormItem>
				)}
			/>

			<DaysBeforeSelect
				name="invoiceFormationDaysBefore"
				label="За сколько дней до оплаты формировать счет"
			/>

			<EnumRadioField
				name="sendToTenantMode"
				label={t('rent.automation.fields.sendToTenant')}
				values={SEND_TO_TENANT_MODES}
				optionLabel={(v) => t(`rent.automation.sendMode.${v}`)}
			/>

			<EnumRadioField
				name="actFormation"
				label={t('rent.automation.fields.actFormation')}
				values={ACT_FORMATIONS}
				optionLabel={(v) => t(`rent.automation.actFormation.${v}`)}
			/>

			<FormField
				name="accrualDistribution"
				render={({ field }) => (
					<FormItem className="flex flex-col gap-2">
						<FormLabel className="text-sm text-(--color-muted-foreground)">
							{t('rent.automation.fields.accrualDistribution')}
						</FormLabel>
						<FormControl>
							<RadioGroup value={field.value} onValueChange={field.onChange} className="flex flex-col gap-2">
								<label spellCheck={false} className="flex items-center gap-2 rounded-(--radius-md) border border-(--color-border) px-4 py-3 cursor-pointer select-none">
									<RadioGroupItem value="ALL_IN_ONE" />
									<span className="text-sm">{t('rent.automation.accrualDistribution.ALL_IN_ONE')}</span>
								</label>
								<label spellCheck={false} className="flex items-center gap-2 rounded-(--radius-md) border border-(--color-border) px-4 py-3 cursor-pointer select-none">
									<RadioGroupItem value="SEPARATE_INVOICE_PER_TYPE" />
									<span className="text-sm">{t('rent.automation.accrualDistribution.SEPARATE_INVOICE_PER_TYPE')}</span>
								</label>
							</RadioGroup>
						</FormControl>
					</FormItem>
				)}
			/>
		</DialogSection>
	)
}

function RemindersBlock({ control }: { control: Control<AutomationUpsertInput> }) {
	const { t } = useTranslation()
	const paymentDay = useWatch({ control, name: 'paymentDay' }) ?? 10
	const reminderBeforeDaysBefore = useWatch({ control, name: 'reminderBeforeDaysBefore' }) ?? 0
	const repeatReminderEveryDays = useWatch({ control, name: 'repeatReminderEveryDays' }) ?? 0
	const dueDate = nextOccurrence(paymentDay)
	const remindDate = addDays(dueDate, -reminderBeforeDaysBefore)
	const repeatDate = addDays(remindDate, repeatReminderEveryDays)

	return (
		<DialogSection
			title={t('rent.automation.sections.reminders')}
			chips={(
				<SummaryChips>
					<SummaryChip label="Напомнить" value={formatRuLong(remindDate)} />
					<SummaryChip label="Повторно напомнить" value={formatRuLong(repeatDate)} />
				</SummaryChips>
			)}
		>
			<DaysBeforeSelect
				name="reminderBeforeDaysBefore"
				label="За сколько дней до оплаты напоминать"
			/>

			<CheckboxField
				name="remindOnDueDate"
				label={t('rent.automation.fields.remindOnDueDate')}
			/>

			<div className="flex flex-col gap-2">
				<span className="text-sm text-(--color-muted-foreground)">
					{t('rent.automation.fields.repeatReminder')}
				</span>
				<div className="grid grid-cols-2 gap-3">
					<FormField
						name="repeatReminderEveryDays"
						render={({ field }) => (
							<FormItem className="flex flex-col gap-1.5">
								<FormLabel className="text-xs text-(--color-muted-foreground)">
									{t('rent.automation.setupDialog.every')}
								</FormLabel>
								<Select
									value={field.value != null ? String(field.value) : ''}
									onValueChange={(v) => field.onChange(Number(v))}
								>
									<FormControl>
										<SelectTrigger size="md">
											<SelectValue />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{REPEAT_EVERY_OPTIONS.map((d) => (
											<SelectItem key={d} value={String(d)}>
												{t('rent.automation.setupDialog.days', { count: d })}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</FormItem>
						)}
					/>
					<FormField
						name="repeatReminderMaxTimes"
						render={({ field }) => (
							<FormItem className="flex flex-col gap-1.5">
								<FormLabel className="text-xs text-(--color-muted-foreground)">
									{t('rent.automation.setupDialog.max')}
								</FormLabel>
								<Select
									value={field.value != null ? String(field.value) : ''}
									onValueChange={(v) => field.onChange(Number(v))}
								>
									<FormControl>
										<SelectTrigger size="md">
											<SelectValue />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{REPEAT_MAX_OPTIONS.map((d) => (
											<SelectItem key={d} value={String(d)}>
												{t('rent.automation.setupDialog.times', { count: d })}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</FormItem>
						)}
					/>
				</div>
			</div>
		</DialogSection>
	)
}

function PaymentsBlock({ rental }: { rental?: Rental }) {
	const { t } = useTranslation()
	const penaltyLabel = rental?.penaltyRate
		? `${rental.penaltyRate}% ${t('rent.terms.basic.perDay').toLowerCase()}`
		: '—'

	return (
		<DialogSection
			title={t('rent.automation.sections.payments')}
			chips={(
				<SummaryChips>
					<SummaryChip label="Аренда" value={formatMoney(rental?.rentAmount, rental?.currency)} />
					<SummaryChip label={t('rent.terms.basic.penalty')} value={penaltyLabel} />
				</SummaryChips>
			)}
		>
			<FormField
				name="paymentDistribution"
				render={({ field }) => (
					<FormItem className="flex flex-col gap-2">
						<FormLabel className="text-sm text-(--color-muted-foreground)">
							Распределение частичной оплаты
						</FormLabel>
						<FormControl>
							<RadioGroup value={field.value} onValueChange={field.onChange} className="flex flex-col gap-2">
								<label spellCheck={false} className="flex items-center gap-2 rounded-(--radius-md) border border-(--color-border) px-4 py-3 cursor-pointer select-none">
									<RadioGroupItem value="RENT_FIRST" />
									<span className="text-sm">{t('rent.automation.paymentDistribution.RENT_FIRST')}</span>
								</label>
								<label spellCheck={false} className="flex items-center gap-2 rounded-(--radius-md) border border-(--color-border) px-4 py-3 cursor-pointer select-none">
									<RadioGroupItem value="PENALTY_FIRST" />
									<span className="text-sm">{t('rent.automation.paymentDistribution.PENALTY_FIRST')}</span>
								</label>
								<label spellCheck={false} className="flex items-center gap-2 rounded-(--radius-md) border border-(--color-border) px-4 py-3 cursor-pointer select-none">
									<RadioGroupItem value="PROPORTIONAL" />
									<span className="text-sm">{t('rent.automation.paymentDistribution.PROPORTIONAL')}</span>
								</label>
							</RadioGroup>
						</FormControl>
					</FormItem>
				)}
			/>
		</DialogSection>
	)
}

function UtilitiesBlock() {
	const { t } = useTranslation()
	return (
		<DialogSection
			title={t('rent.automation.sections.utilities')}
			chips={(
				<SummaryChips>
					<SummaryChip label="Электроэнергия" value="По счетчику" />
					<SummaryChip label="Водоснабжение" value="По счетчику" />
				</SummaryChips>
			)}
		>
			<DaysBeforeSelect
				name="utilityReadingsRequestDaysBefore"
				label="За сколько дней до расчета запрашивать показания"
			/>

			<CheckboxField
				name="utilityRemindOnCalculationDay"
				label={t('rent.automation.fields.remindOnCalculationDay')}
			/>
		</DialogSection>
	)
}

function CompletionBlock({ rental }: { rental?: Rental }) {
	const { t } = useTranslation()
	const penaltyLabel = rental?.penaltyRate
		? `${rental.penaltyRate}% ${t('rent.terms.basic.perDay').toLowerCase()}`
		: '—'

	return (
		<DialogSection
			title={t('rent.automation.sections.completion')}
			chips={(
				<SummaryChips>
					<SummaryChip label="Аренда" value={formatMoney(rental?.rentAmount, rental?.currency)} />
					<SummaryChip label={t('rent.terms.basic.penalty')} value={penaltyLabel} />
				</SummaryChips>
			)}
		>
			<FormField
				name="completionReminderDaysBefore"
				render={({ field }) => (
					<FormItem className="flex flex-col gap-1.5">
						<FormLabel className="text-sm text-(--color-muted-foreground)">
							За сколько дней до окончания напомнить
						</FormLabel>
						<Select
							value={field.value != null ? String(field.value) : ''}
							onValueChange={(v) => field.onChange(Number(v))}
						>
							<FormControl>
								<SelectTrigger size="md">
									<SelectValue />
								</SelectTrigger>
							</FormControl>
							<SelectContent>
								{COMPLETION_OPTIONS.map((d) => (
									<SelectItem key={d} value={String(d)}>
										{t('rent.automation.values.daysBeforeEnd', { count: d })}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</FormItem>
				)}
			/>
		</DialogSection>
	)
}
